import React from "react";
import {
  Box,
  Button,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  Flashbar,
} from "@amzn/awsui-components-react";
import Multiselect from "@amzn/awsui-components-core/multiselect";
import Select from "@amzn/awsui-components-core/select";
import { COLUMN_DEFINITIONS } from "../config/searchTableConfig";
import { useSearchHistory } from "../hooks/useSearchHistory";
import useFolders from "../hooks/useFolders";
import { SavedSearchItem, SearchHistoryItem } from "../types/common";
import { FolderModal } from "./folders/FolderModal";
import { useFolderUI } from "../hooks/useFolderUI";

export default function SearchHistory() {
  const {
    isModalVisible,
    isFolderModalVisible,
    selectedFolderId,
    folderName,
    error,
    setIsModalVisible,
    setSelectedFolderId,
    setFolderName,
    setNotification,
    handleModalDismiss,
    handleCreateFolder,
    handleFolderModalDismiss,
    handleSaveFolder,
    getFlashbarItems,
    flashbarStyles,
  } = useFolderUI();

  const {
    items,
    collectionProps,
    paginationProps,
    deleteSelectedItems,
    clearFilters,
    filters,
    filterOptions,
    updateFilter,
    updateMarketplaces,
  } = useSearchHistory();
  const { folders, addTransactions, saveFolder } = useFolders();

  const handleAddToFolder = () => {
    if (!collectionProps.selectedItems?.length) {
      return;
    }
    setIsModalVisible(true);
  };

  const handleFolderSelect = () => {
    if (selectedFolderId && collectionProps.selectedItems?.length) {
      const selectedTransactions = (
        collectionProps.selectedItems as SearchHistoryItem[]
      ).map(
        (item) =>
          ({
            id: item.orderId,
            stage: item.stage,
            status: item.status,
            country: item.countryCode || "-",
            gl: item.glProductGroup,
            usecase: "",
            paymentMethod: "",
          } as SavedSearchItem)
      );

      const result = addTransactions(selectedFolderId, selectedTransactions);

      setNotification({
        type: result.success ? "success" : "error",
        message: result.message,
      });

      if (result.addedCount > 0 && collectionProps.onSelectionChange) {
        collectionProps.onSelectionChange({ detail: { selectedItems: [] } });
      }
      handleModalDismiss();
    }
  };

  return (
    <>
      <div style={flashbarStyles}>
        <Flashbar items={getFlashbarItems()} />
      </div>
      <Table
        {...collectionProps}
        resizableColumns
        stickyHeader
        columnDefinitions={COLUMN_DEFINITIONS}
        items={items}
        loadingText="Loading resources"
        selectionType="multi"
        header={
          <Header
            variant="awsui-h1-sticky"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={handleAddToFolder}
                  disabled={collectionProps.selectedItems?.length === 0}
                >
                  Add to Folder
                </Button>
                <Button
                  onClick={deleteSelectedItems}
                  disabled={collectionProps.selectedItems?.length === 0}
                >
                  Delete selected
                </Button>
              </SpaceBetween>
            }
          >
            Recent Searches
          </Header>
        }
        filter={
          <div className="input-container">
            <div className="select-filter" style={{ maxInlineSize: "110px" }}>
              <Select
                data-testid="stage-filter"
                inlineLabelText="Stage"
                options={filterOptions.stage}
                selectedAriaLabel="Selected"
                selectedOption={filters.stage}
                onChange={({ detail }) =>
                  updateFilter("stage", detail.selectedOption)
                }
                expandToViewport
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "180px" }}>
              <Select
                data-testid="gl-filter"
                inlineLabelText="GL"
                options={filterOptions.gl}
                selectedAriaLabel="Selected"
                selectedOption={filters.gl}
                onChange={({ detail }) =>
                  updateFilter("gl", detail.selectedOption)
                }
                expandToViewport
                filteringType="auto"
                filteringPlaceholder="Find GL"
                empty="No matching GLs"
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "220px" }}>
              <Multiselect
                placeholder="Marketplaces"
                inlineTokens
                data-testid="marketplace-filter"
                options={filterOptions.marketplaces}
                selectedOptions={filters.marketplaces}
                onChange={({ detail }) =>
                  updateMarketplaces(detail.selectedOptions)
                }
                filteringType="auto"
                expandToViewport
                filteringPlaceholder="Find marketplaces"
                empty="No matching marketplaces"
              />
            </div>
            <div className="select-filter" style={{ maxInlineSize: "140px" }}>
              <Select
                data-testid="status-filter"
                inlineLabelText="Status"
                options={filterOptions.status}
                selectedAriaLabel="Selected"
                selectedOption={filters.status}
                onChange={({ detail }) =>
                  updateFilter("status", detail.selectedOption)
                }
                expandToViewport
              />
            </div>
            <Button onClick={clearFilters}>Clear filters</Button>
          </div>
        }
        pagination={<Pagination {...paginationProps} />}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No recent searches</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              Transactions you search for will appear here.
            </Box>
          </Box>
        }
      />

      <FolderModal
        isModalVisible={isModalVisible}
        isFolderModalVisible={isFolderModalVisible}
        isShareModalVisible={false}
        selectedFolderId={selectedFolderId}
        folderName={folderName}
        error={error}
        folders={folders}
        sharingFolder={null}
        onModalDismiss={handleModalDismiss}
        onCreateFolder={handleCreateFolder}
        onFolderSelect={handleFolderSelect}
        onFolderModalDismiss={handleFolderModalDismiss}
        onShareModalDismiss={() => {}}
        onSaveFolder={() => handleSaveFolder(saveFolder)}
        setSelectedFolderId={setSelectedFolderId}
        setFolderName={setFolderName}
      />
    </>
  );
}
